/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Layout,
  Button,
  Table,
  Tag,
  Space,
  Popconfirm,
  Modal,
  DatePicker,
  InputNumber,
  Input,
  Tooltip,
  message,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  StarFilled,
  StarOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

import SiderComponent from '../components/SiderComponent';
import HeaderComponent from '../components/HeaderComponent';
import greenfeeDealsApi from '../api/greenfeeDeals';
import { setGolfClubDetails } from '../redux/actions/golfClubActions';

const { Content } = Layout;
const { RangePicker } = DatePicker;

function isDealOnline(deal) {
  if (!deal?.dealPeriod?.from || !deal?.dealPeriod?.to) return false;
  const today = moment().startOf('day');
  const from = moment(deal.dealPeriod.from).startOf('day');
  const to = moment(deal.dealPeriod.to).endOf('day');
  if (to.isBefore(today)) return false;
  if (from.isAfter(today)) return true;
  return today.isBetween(from, to, null, '[]');
}

function isFeaturedActive(deal) {
  if (!deal.isFeatured || !deal.featuredPeriod?.to) return false;
  return new Date(deal.featuredPeriod.to) >= new Date();
}

const categoryColors = {
  'Events & Turniere': '#60BC9D',
  'Kurse & Reisen': '#50C0F8',
  Equipment: '#FFC145',
  'Greenfee-Specials': '#AF89E5',
};

const PKG_COLORS = {
  basic:      { bg: '#f0f0f0', activeBg: '#e8e8e8', border: '#bbb',    activeBorder: '#888',    text: '#555',    badge: '#888' },
  pro:        { bg: '#fff',    activeBg: '#e6f4ff', border: '#91caff', activeBorder: '#1677ff', text: '#1677ff', badge: '#1677ff' },
  premium:    { bg: '#fff',    activeBg: '#f3eaff', border: '#c9a8f5', activeBorder: '#7c3aed', text: '#7c3aed', badge: '#7c3aed' },
  enterprise: { bg: '#fff',    activeBg: '#fff8e6', border: '#ffd666', activeBorder: '#d4900a', text: '#d4900a', badge: '#d4900a' },
};

const PKG_RANK = { basic: 0, pro: 1, premium: 2, enterprise: 3 };

const PKG_PRICES = {
  basic: 'Kostenlos',
  pro: '239 € zzgl. MwSt / Jahr',
  premium: '449 € zzgl. MwSt / Jahr',
};

const PKG_PRICE_AMOUNTS = {
  pro: '239 €',
  premium: '449 €',
};

const TOP_PLACEMENT_TOOLTIP = (count, days) =>
  count === 0
    ? 'Ihre Deals werden prominent auf der Plattform und in der App platziert, was die Sichtbarkeit erhöht. Im Basic-Paket nicht verfügbar.'
    : `Ihre Deals werden prominent auf der Plattform und in der App platziert, was die Sichtbarkeit erhöht.\nIn Ihrem Paket ist das bei ${count} Deals pro Jahr möglich, je für ${days} Tage.`;

const NOTIFICATION_TOOLTIP = 'Ein erstellter Deal wird automatisch an Golfer in Ihrer Region gesendet (App).';
const POWER_PUSH_TOOLTIP = 'Zusätzlich zur Notification können Sie eine weitere Nachricht senden (z.B. kurz vor dem Ereignis), um zu erinnern oder nochmal zu aktivieren. Der Zeitpunkt ist wählbar.';

const PKG_FEATURES = {
  basic: [
    { label: 'Deals', value: 'Unbegrenzt' },
    { label: 'Top-Platzierungen', value: 'Keine', tooltip: TOP_PLACEMENT_TOOLTIP(0) },
    { label: 'Notification', value: 'Nein', tooltip: NOTIFICATION_TOOLTIP },
    { label: 'Power-Push', value: 'Nein', tooltip: POWER_PUSH_TOOLTIP },
  ],
  pro: [
    { label: 'Deals', value: 'Unbegrenzt' },
    { label: 'Top-Platzierungen', value: '5x für je 4 Tage', tooltip: TOP_PLACEMENT_TOOLTIP(5, 4) },
    { label: 'Notification', value: 'Ja', tooltip: NOTIFICATION_TOOLTIP },
    { label: 'Power-Push', value: 'Nein', tooltip: POWER_PUSH_TOOLTIP },
  ],
  premium: [
    { label: 'Deals', value: 'Unbegrenzt' },
    { label: 'Top-Platzierungen', value: '10x für je 10 Tage', tooltip: TOP_PLACEMENT_TOOLTIP(10, 10) },
    { label: 'Notification', value: 'Ja', tooltip: NOTIFICATION_TOOLTIP },
    { label: 'Power-Push', value: '10x', tooltip: POWER_PUSH_TOOLTIP },
  ],
};

function FeatureList({ pkgKey, color }) {
  const features = PKG_FEATURES[pkgKey] || [];
  return (
    <div style={{ fontSize: 12, lineHeight: 1.9 }}>
      {features.map((f) => (
        <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, whiteSpace: 'nowrap' }}>
          <span style={{ color: '#888' }}>
            {f.label}
            {f.tooltip && (
              <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{f.tooltip}</span>} placement="right">
                <InfoCircleOutlined style={{ fontSize: 9, color: '#bbb', marginLeft: 3, verticalAlign: 'super', cursor: 'help' }} />
              </Tooltip>
            )}
          </span>
          <span style={{ fontWeight: 600, color: color || '#333' }}>{f.value}</span>
        </div>
      ))}
    </div>
  );
}

function PackageBanner({ packages, activeKey, pendingKey, expiresAt, onOpenDetails, onOpenEnterpriseContact }) {
  if (!packages || packages.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.1em' }}>
        Ihr Paket
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {packages.map((pkg) => {
          const isActive = pkg.key === (activeKey || 'basic');
          const isPending = pkg.key === pendingKey;
          const c = PKG_COLORS[pkg.key] || PKG_COLORS.basic;
          return (
            <div
              key={pkg.key}
              style={{
                border: `2px solid ${isActive ? c.activeBorder : c.border}`,
                borderRadius: 12,
                padding: '16px 20px',
                background: isActive ? c.activeBg : c.bg,
                minWidth: 240,
                flex: '1 1 240px',
                maxWidth: 300,
                position: 'relative',
                boxShadow: isActive ? `0 2px 12px ${c.activeBorder}44` : 'none',
                transition: 'all .15s',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {isActive && (
                <Tooltip title={expiresAt ? `Laufzeit bis ${new Date(expiresAt).toLocaleDateString('de-DE')}` : null}>
                  <span style={{
                    position: 'absolute', top: -11, left: 14,
                    background: c.badge, color: '#fff',
                    fontSize: 9, fontWeight: 800, padding: '2px 9px',
                    borderRadius: 10, letterSpacing: '.08em', textTransform: 'uppercase',
                    cursor: expiresAt ? 'help' : 'default',
                  }}>
                    ✓ Aktiv
                  </span>
                </Tooltip>
              )}
              {isPending && !isActive && (
                <span style={{
                  position: 'absolute', top: -11, left: 14,
                  background: '#faad14', color: '#fff',
                  fontSize: 9, fontWeight: 800, padding: '2px 9px',
                  borderRadius: 10, letterSpacing: '.08em', textTransform: 'uppercase',
                }}>
                  ⏳ Vorgemerkt
                </span>
              )}
              <div style={{
                fontWeight: 800, fontSize: 16,
                color: isActive ? c.text : '#555',
                marginBottom: 8,
              }}>
                {pkg.name}
              </div>

              {pkg.key === 'enterprise' ? (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 1.7, flex: 1 }}>
                  Nehmen Sie Kontakt mit unserem Team auf, um für Ihre individuellen Bedürfnisse die passende Lösung zu finden.
                </div>
              ) : (
                <div style={{ marginBottom: 10, flex: 1 }}>
                  <FeatureList pkgKey={pkg.key} color={isActive ? c.text : undefined} />
                  <div style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: `1px solid ${isActive ? c.activeBorder + '44' : '#eee'}`,
                    fontSize: 12,
                    fontWeight: 700,
                    color: isActive ? c.text : '#555',
                    textAlign: 'right',
                  }}>
                    {PKG_PRICES[pkg.key]}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                {pkg.key === 'enterprise' ? (
                  <Button
                    size="small"
                    onClick={() => onOpenEnterpriseContact()}
                    style={{
                      fontSize: 12, fontWeight: 700,
                      borderColor: c.activeBorder,
                      color: c.text,
                      borderRadius: 6,
                    }}
                  >
                    Anfrage senden
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => onOpenDetails(pkg)}
                    style={{
                      fontSize: 12, fontWeight: 600,
                      borderColor: isActive ? c.activeBorder : '#d9d9d9',
                      color: isActive ? c.text : '#555',
                      borderRadius: 6,
                    }}
                  >
                    Details & Buchen
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopDealsList(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [packages, setPackages] = useState([]);
  const [detailsModal, setDetailsModal] = useState({ visible: false, pkg: null });
  const [activateModal, setActivateModal] = useState({ visible: false, pkg: null, isDowngrade: false });
  const [enterpriseModal, setEnterpriseModal] = useState({ visible: false, contactPerson: '', phone: '', loading: false });
  const [featuredConfirmModal, setFeaturedConfirmModal] = useState({ visible: false, deal: null, remaining: 0 });
  const [featuredModal, setFeaturedModal] = useState({ visible: false, deal: null });
  const [featuredRange, setFeaturedRange] = useState(null);
  const [powerPushDays, setPowerPushDays] = useState(1);
  const [powerPushModal, setPowerPushModal] = useState({ visible: false, deal: null, date: null, loading: false });
  const [loadingActivate, setLoadingActivate] = useState(false);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ visible: false, suggestedKeys: [] });

  const golfClubDetails = useSelector((state) => state.golfClub.golfClubDetails);
  const sessionBnd = localStorage.getItem('sessionBnd');
  const headers = { headers: { Authorization: `Bearer ${sessionBnd}` } };

  useEffect(() => {
    greenfeeDealsApi.get('/packages').then((res) => {
      setPackages(res.data.data.data || []);
    }).catch(() => {});
  }, []);

  const appLanguage = document.documentElement.lang || navigator.language || 'en';
  const isGerman = appLanguage.toLowerCase().startsWith('de');

  const t = {
    name: 'Name',
    category: 'Kategorie',
    price: 'Preis / Rabatt',
    duration: 'Zeitraum',
    status: 'Status',
    actions: 'Bearbeiten',
    online: 'Online',
    offline: 'Offline',
    freeDeal: 'Kostenloser Deal',
    heading: 'Top Deals',
    subHeading:
      'Demo-Days, Schnupperkurse oder Proshop-Sales – Mit wenigen Klicks können Sie Ihre temporären Aktionen, Events, Kursangebote oder Greenfee-Specials direkt an alle Golfer kommunizieren.',
    addDeal: 'Top Deal erstellen',
  };

  if (!golfClubDetails) {
    return <div style={{ padding: 50 }}>Loading deals...</div>;
  }

  const golfClubId = golfClubDetails._id.golfClub;
  const activePackageKey = golfClubDetails.activePackageKey || 'basic';
  const pendingPackageKey = golfClubDetails.pendingPackageKey || null;
  const featuredLimit = golfClubDetails.package?.featuredDealsLimit ?? 0;
  const packageExpiresAt = golfClubDetails.packageExpiresAt || null;
  const powerPushLimit = golfClubDetails.package?.pushNotificationsLimit ?? 0;
  const powerPushSentCount = golfClubDetails.powerPushSentCount ?? 0;
  const powerPushRemaining = Math.max(0, powerPushLimit - powerPushSentCount);
  const featuredUsedCount = golfClubDetails.featuredUsedCount ?? 0;
  const activeFeaturedCount = (golfClubDetails.topDeals || []).filter(isFeaturedActive).length;

  const refreshClub = async () => {
    const res = await greenfeeDealsApi.post('/golfclubs/golfClubAdmin', { id: golfClubId }, headers);
    dispatch(setGolfClubDetails(res.data.data.data));
  };

  const deleteDeal = async (dealId) => {
    await greenfeeDealsApi.delete(`/golfclubs/${golfClubId}/topDeals/${dealId}`, headers);
    await refreshClub();
  };

  const activateFeatured = async (dealId, period, ppDays) => {
    setLoadingFeatured(true);
    try {
      await greenfeeDealsApi.patch(
        `/golfclubs/${golfClubId}/topDeals/${dealId}`,
        {
          isFeatured: true,
          featuredPeriod: period,
          powerPushDaysBefore: ppDays || null,
        },
        headers
      );
      await refreshClub();
      message.success('Top-Platzierung aktiviert');
    } catch (err) {
      message.error(err.response?.data?.message || 'Fehler beim Aktivieren');
    } finally {
      setLoadingFeatured(false);
    }
  };

  const deactivateFeatured = async (dealId) => {
    try {
      await greenfeeDealsApi.patch(
        `/golfclubs/${golfClubId}/topDeals/${dealId}`,
        { isFeatured: false },
        headers
      );
      await refreshClub();
      message.success('Top-Platzierung beendet');
    } catch {
      message.error('Fehler');
    }
  };

  const savePowerPush = async () => {
    const { deal, date } = powerPushModal;
    if (!date) return;
    setPowerPushModal((m) => ({ ...m, loading: true }));
    try {
      await greenfeeDealsApi.post(
        `/golfclubs/${golfClubId}/topdeals/${deal._id}/power-push`,
        { scheduledAt: date.toISOString() },
        headers
      );
      await refreshClub();
      message.success('Power-Push gesetzt');
      setPowerPushModal({ visible: false, deal: null, date: null, loading: false });
    } catch (err) {
      message.error(err.response?.data?.message || 'Fehler beim Speichern');
      setPowerPushModal((m) => ({ ...m, loading: false }));
    }
  };

  const cancelPowerPush = async (dealId) => {
    try {
      await greenfeeDealsApi.post(
        `/golfclubs/${golfClubId}/topdeals/${dealId}/power-push`,
        { scheduledAt: null },
        headers
      );
      await refreshClub();
      message.success('Power-Push entfernt');
    } catch {
      message.error('Fehler');
    }
  };

  const handleFeatureClick = (deal) => {
    const remaining = Math.max(0, featuredLimit - featuredUsedCount);
    setFeaturedConfirmModal({ visible: true, deal, remaining });
  };

  const handleFeatureConfirmed = () => {
    const { deal } = featuredConfirmModal;
    setFeaturedConfirmModal({ visible: false, deal: null, remaining: 0 });
    const now = new Date();
    const featuredDays = activePackageKey === 'pro' ? 4 : 10;
    const featuredEnd = new Date(now.getTime() + featuredDays * 24 * 60 * 60 * 1000);
    const dealEnd = deal.dealPeriod?.to ? new Date(deal.dealPeriod.to) : null;
    const to = dealEnd && dealEnd < featuredEnd ? dealEnd : featuredEnd;
    activateFeatured(deal._id, { from: now, to }, null);
  };

  const handleOpenDetails = (pkg) => {
    setDetailsModal({ visible: true, pkg });
  };

  const handleOpenEnterpriseContact = () => {
    setEnterpriseModal({ visible: true, contactPerson: '', phone: '', loading: false });
  };

  const handleSendEnterpriseContact = async () => {
    if (!enterpriseModal.contactPerson.trim()) {
      message.warning('Bitte Ansprechpartner angeben');
      return;
    }
    if (!enterpriseModal.phone.trim()) {
      message.warning('Bitte Rückrufnummer angeben');
      return;
    }
    const enterprisePkg = packages.find((p) => p.key === 'enterprise');
    if (!enterprisePkg) return;
    setEnterpriseModal((m) => ({ ...m, loading: true }));
    try {
      await greenfeeDealsApi.post(
        `/golfclubs/${golfClubId}/activate-package`,
        { packageId: enterprisePkg._id, contactPerson: enterpriseModal.contactPerson, phone: enterpriseModal.phone },
        headers
      );
      setEnterpriseModal({ visible: false, contactPerson: '', phone: '', loading: false });
      message.success('Ihre Anfrage wurde gesendet. Wir melden uns bei Ihnen!');
    } catch (err) {
      message.error(err.response?.data?.message || 'Fehler beim Senden');
      setEnterpriseModal((m) => ({ ...m, loading: false }));
    }
  };

  const handleActivateFromDetails = () => {
    const pkg = detailsModal.pkg;
    const isDowngrade = (PKG_RANK[pkg.key] ?? 0) < (PKG_RANK[activePackageKey] ?? 0);
    setDetailsModal({ visible: false, pkg: null });
    setActivateModal({ visible: true, pkg, isDowngrade });
  };

  const handleActivatePackage = async () => {
    const { pkg, isDowngrade } = activateModal;
    setLoadingActivate(true);
    try {
      await greenfeeDealsApi.post(
        `/golfclubs/${golfClubId}/activate-package`,
        { packageId: pkg._id },
        headers
      );
      await refreshClub();
      setActivateModal({ visible: false, pkg: null, isDowngrade: false });
      if (isDowngrade) {
        message.success('Ihre Tarifanpassung wurde vorgemerkt und startet nach Ablauf des aktiven Pakets automatisch.');
      } else if (pkg.key === 'enterprise') {
        message.success('Kontaktanfrage gesendet! Wir melden uns bei Ihnen.');
      } else {
        message.success(`Paket „${pkg.name}" erfolgreich aktiviert!`);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Fehler');
    } finally {
      setLoadingActivate(false);
    }
  };

  const formatDealPrice = (deal) => {
    const value = deal.discountPercent || deal.discountPrice;
    if (!value) return t.freeDeal;
    const formatted = new Intl.NumberFormat(isGerman ? 'de-DE' : 'en-US').format(Number(value));
    if (deal.discountType === '%') return `${formatted}%`;
    return `${formatted} €`;
  };

  const columns = [
    {
      title: t.name,
      key: 'deal',
      width: '22%',
      render: (deal) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 14, color: '#222', marginBottom: 4 }}>
            {deal.title}
          </div>
          <div style={{ fontSize: 12, color: '#628478', lineHeight: 1.4 }}>
            {deal.description?.slice(0, 50)}...
          </div>
        </div>
      ),
    },
    {
      title: t.category,
      dataIndex: 'category',
      key: 'category',
      width: '14%',
      render: (cat) => (
        <Tag
          style={{
            backgroundColor: categoryColors[cat] || '#52C41A',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            padding: '2px 12px',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {cat}
        </Tag>
      ),
    },
    {
      title: t.price,
      key: 'price',
      width: '11%',
      render: (deal) => (
        <span style={{ fontSize: 14, fontWeight: 500 }}>{formatDealPrice(deal)}</span>
      ),
    },
    {
      title: t.duration,
      key: 'duration',
      width: '17%',
      render: (deal) => (
        <div style={{ fontSize: 12, color: '#628478' }}>
          <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CalendarOutlined style={{ color: '#628478' }} />
            <span>
              {moment(deal?.dealPeriod?.from).format('DD-MM-YYYY')} /{' '}
              {moment(deal?.dealPeriod?.to).format('DD-MM-YYYY')}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClockCircleOutlined style={{ color: '#628478' }} />
            <span>
              {deal.startTime || '00:00'} - {deal.endTime || '23:59'}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: t.status,
      key: 'status',
      width: '9%',
      render: (deal) => {
        const online = isDealOnline(deal);
        return (
          <Tag
            style={{
              backgroundColor: online ? '#EBFFF4' : '#FFF2F2',
              color: online ? '#2BAB60' : '#DF3A3A',
              borderRadius: 13,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 600,
              border: 0,
            }}
          >
            {online ? t.online : t.offline}
          </Tag>
        );
      },
    },
    {
      title: (
        <div>
          <div>Top-Platzierung</div>
          {featuredLimit > 0 && (
            <div style={{ fontSize: 10, color: '#888', fontWeight: 400, marginTop: 2 }}>
              {Math.max(0, featuredLimit - featuredUsedCount)} verbleibend
            </div>
          )}
        </div>
      ),
      key: 'featured',
      width: '11%',
      align: 'center',
      render: (deal) => {
        const active = isFeaturedActive(deal);

        if (featuredLimit === 0) {
          return (
            <Button
              size="small"
              onClick={() => setUpgradeModal({ visible: true, suggestedKeys: ['pro', 'premium'] })}
              style={{ fontSize: 11, borderRadius: 6, color: '#1677ff', borderColor: '#91caff' }}
            >
              Upgraden
            </Button>
          );
        }

        if (Math.max(0, featuredLimit - featuredUsedCount) === 0 && activePackageKey === 'pro') {
          return (
            <Button
              size="small"
              onClick={() => setUpgradeModal({ visible: true, suggestedKeys: ['premium'] })}
              style={{ fontSize: 11, borderRadius: 6, color: '#7c3aed', borderColor: '#c9a8f5' }}
            >
              Upgraden
            </Button>
          );
        }

        if (active) {
          const until = moment(deal.featuredPeriod.to).format('DD.MM.YY');
          return (
            <Popconfirm
              title="Top-Platzierung beenden?"
              onConfirm={() => deactivateFeatured(deal._id)}
              okText="Ja"
              cancelText="Nein"
            >
              <Button
                size="small"
                type="link"
                icon={<StarFilled />}
                style={{ color: '#52C41A', fontWeight: 600, fontSize: 11, paddingLeft: 0 }}
              >
                Aktiv bis {until}
              </Button>
            </Popconfirm>
          );
        }

        if (!isDealOnline(deal)) {
          return (
            <Button size="small" disabled style={{ fontSize: 11 }}>
              Hervorheben
            </Button>
          );
        }

        return (
          <Button
            size="small"
            icon={<StarOutlined />}
            onClick={() => handleFeatureClick(deal)}
            style={{ fontSize: 11, borderColor: '#628478', color: '#628478' }}
          >
            Hervorheben
          </Button>
        );
      },
    },
    {
      title: (
        <div>
          <div>Power-Push</div>
          {activePackageKey === 'premium' && powerPushLimit > 0 && (
            <div style={{ fontSize: 10, color: '#888', fontWeight: 400, marginTop: 2 }}>
              {powerPushRemaining} verbleibend
            </div>
          )}
        </div>
      ),
      key: 'powerPush',
      width: '13%',
      align: 'center',
      render: (deal) => {
        if (!isDealOnline(deal)) {
          return <span style={{ fontSize: 11, color: '#ccc' }}>—</span>;
        }
        if (activePackageKey !== 'premium') {
          return (
            <Button
              size="small"
              onClick={() => setUpgradeModal({ visible: true, suggestedKeys: ['premium'] })}
              style={{ fontSize: 11, borderRadius: 6, color: '#1677ff', borderColor: '#91caff' }}
            >
              Upgraden
            </Button>
          );
        }
        // Push wurde gesendet
        if (deal.powerPushSentAt) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#389e0d', fontWeight: 600 }}>
                am {new Date(deal.powerPushSentAt).toLocaleDateString('de-DE')} gesendet
              </span>
              {powerPushRemaining > 0 ? (
                <Button
                  size="small"
                  onClick={() => setPowerPushModal({ visible: true, deal, date: null, loading: false })}
                  style={{ fontSize: 10, borderRadius: 6, borderColor: '#c9a8f5', color: '#7c3aed', height: 22, padding: '0 8px' }}
                >
                  weiteren Push planen
                </Button>
              ) : (
                <span style={{ fontSize: 10, color: '#bbb' }}>Kontingent aufgebraucht</span>
              )}
            </div>
          );
        }
        // Push geplant (noch nicht gesendet) — Klick öffnet Date-Picker zum Bearbeiten
        if (deal.powerPushScheduledAt) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <Tag
                onClick={() => setPowerPushModal({ visible: true, deal, date: new Date(deal.powerPushScheduledAt), loading: false })}
                style={{ fontSize: 11, borderRadius: 8, background: '#f3eaff', border: '1px solid #c9a8f5', color: '#7c3aed', cursor: 'pointer', margin: 0 }}
              >
                Push am {new Date(deal.powerPushScheduledAt).toLocaleDateString('de-DE')}
              </Tag>
              <Popconfirm
                title="Power-Push entfernen?"
                onConfirm={() => cancelPowerPush(deal._id)}
                okText="Entfernen"
                cancelText="Abbrechen"
              >
                <span style={{ fontSize: 10, color: '#bbb', cursor: 'pointer', textDecoration: 'underline' }}>entfernen</span>
              </Popconfirm>
            </div>
          );
        }
        // Noch kein Push geplant
        return powerPushRemaining > 0 ? (
          <Button
            size="small"
            onClick={() => setPowerPushModal({ visible: true, deal, date: null, loading: false })}
            style={{ fontSize: 11, borderRadius: 6, borderColor: '#c9a8f5', color: '#7c3aed' }}
          >
            Push setzen
          </Button>
        ) : (
          <span style={{ fontSize: 11, color: '#bbb' }}>Kontingent aufgebraucht</span>
        );
      },
    },
    {
      title: t.actions,
      key: 'actions',
      width: '10%',
      align: 'center',
      render: (deal) => (
        <Space size={8}>
          <Button
            type="text"
            icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M14.1017 1.55629C13.5644 1.01929 12.8358 0.717636 12.0761 0.717636C11.3164 0.717636 10.5878 1.01929 10.0504 1.55629L9.41345 2.19324L9.40388 2.18367L8.27673 3.31082L8.28523 3.32145L2.03275 9.57393C1.48468 10.1223 1.17678 10.8658 1.17676 11.6411V13.6848C1.17676 13.8963 1.26078 14.0992 1.41034 14.2488C1.55991 14.3983 1.76276 14.4823 1.97427 14.4823H4.01802C4.79345 14.482 5.53701 14.1737 6.08517 13.6253L14.1028 5.60871C14.6398 5.07134 14.9415 4.34273 14.9415 3.58303C14.9415 2.82334 14.6398 2.09473 14.1028 1.55736L14.1017 1.55629ZM9.41345 4.4486L3.16096 10.7011C3.03755 10.8245 2.93965 10.9711 2.87288 11.1324C2.8061 11.2937 2.77175 11.4665 2.77178 11.6411V12.8873H4.01802C4.36999 12.8873 4.7092 12.747 4.95696 12.4981L11.2094 6.24565L9.41345 4.4486ZM12.3377 5.11744L12.9746 4.4805C13.0972 4.36356 13.1952 4.22328 13.2628 4.06792C13.3304 3.91255 13.3662 3.74524 13.3682 3.57581C13.3702 3.40639 13.3383 3.23829 13.2743 3.08138C13.2104 2.92448 13.1157 2.78195 12.9958 2.66218C12.876 2.54241 12.7334 2.44781 12.5765 2.38396C12.4195 2.32011 12.2514 2.28828 12.082 2.29036C11.9126 2.29244 11.7453 2.32838 11.5899 2.39607C11.4346 2.46376 11.2944 2.56183 11.1775 2.6845L10.5417 3.32145L12.3377 5.11744Z" fill="#628478" stroke="#F6F8F7" strokeWidth="0.5"/>
              </svg>
            }
            onClick={() => navigate(`/deals/top-deals/edit/${deal._id}`)}
            style={{ padding: '4px 8px' }}
            className="btn-style-common"
          />
          <Popconfirm title="Deal löschen?" onConfirm={() => deleteDeal(deal._id)} okText="Ja" cancelText="Nein">
            <Button
              type="text"
              icon={
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.45208 6.53325C9.64476 6.53325 9.82955 6.60173 9.96579 6.72364C10.102 6.84554 10.1786 7.01087 10.1786 7.18327V10.8667C10.1786 11.0391 10.102 11.2045 9.96579 11.3264C9.82955 11.4483 9.64476 11.5168 9.45208 11.5168C9.2594 11.5168 9.07462 11.4483 8.93837 11.3264C8.80213 11.2045 8.72559 11.0391 8.72559 10.8667V7.18327C8.72559 7.01087 8.80213 6.84554 8.93837 6.72364C9.07462 6.60173 9.2594 6.53325 9.45208 6.53325Z" fill="#628478" stroke="#F6F8F7" strokeWidth="0.5"/>
                  <path d="M7.27282 7.18327C7.27282 7.01087 7.19627 6.84554 7.06003 6.72364C6.92379 6.60173 6.739 6.53325 6.54632 6.53325C6.35364 6.53325 6.16885 6.60173 6.03261 6.72364C5.89637 6.84554 5.81982 7.01087 5.81982 7.18327V10.8667C5.81982 11.0391 5.89637 11.2045 6.03261 11.3264C6.16885 11.4483 6.35364 11.5168 6.54632 11.5168C6.739 11.5168 6.92379 11.4483 7.06003 11.3264C7.19627 11.2045 7.27282 11.0391 7.27282 10.8667V7.18327Z" fill="#628478" stroke="#F6F8F7" strokeWidth="0.5"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.72074 3.20872C4.72074 2.42402 5.06616 1.67146 5.68101 1.11659C6.29585 0.561722 7.12976 0.25 7.99929 0.25C8.86881 0.25 9.70272 0.561722 10.3176 1.11659C10.9324 1.67146 11.2778 2.42402 11.2778 3.20872H14.8544C15.0916 3.20872 15.319 3.29374 15.4867 3.44507C15.6544 3.59639 15.7486 3.80164 15.7486 4.01565C15.7486 4.22966 15.6544 4.4349 15.4867 4.58623C15.319 4.73756 15.0916 4.82257 14.8544 4.82257H13.9603V10.6862C13.9603 12.4937 13.9603 13.3975 13.5704 14.0882C13.2275 14.6955 12.6804 15.1893 12.0075 15.4987C11.2421 15.8505 10.2406 15.8505 8.23773 15.8505H7.76085C5.75796 15.8505 4.75651 15.8505 3.99112 15.4987C3.31817 15.1893 2.77104 14.6955 2.42815 14.0882C2.0383 13.3975 2.0383 12.4937 2.0383 10.6862V4.82257H1.14415C0.907006 4.82257 0.679575 4.73756 0.51189 4.58623C0.344205 4.4349 0.25 4.22966 0.25 4.01565C0.25 3.80164 0.344205 3.59639 0.51189 3.44507C0.679575 3.29374 0.907006 3.20872 1.14415 3.20872H4.72074ZM6.50904 3.20872C6.50904 2.85204 6.66605 2.50997 6.94552 2.25775C7.225 2.00554 7.60405 1.86385 7.99929 1.86385C8.39453 1.86385 8.77358 2.00554 9.05305 2.25775C9.33253 2.50997 9.48953 2.85204 9.48953 3.20872H6.50904ZM3.82659 4.82257H12.172V10.6862C12.172 11.6169 12.1708 12.2172 12.1291 12.6745C12.0897 13.1135 12.0218 13.2759 11.9765 13.3555C11.8051 13.6589 11.5317 13.9056 11.1956 14.0602C11.1073 14.1011 10.9273 14.1625 10.4409 14.198C9.93423 14.2356 9.26898 14.2367 8.23773 14.2367H7.76085C6.7296 14.2367 6.06435 14.2356 5.55767 14.198C5.07125 14.1625 4.89123 14.1011 4.803 14.0602C4.4664 13.9058 4.19263 13.6591 4.02092 13.3555C3.97681 13.2759 3.90886 13.1135 3.86951 12.6745C3.82779 12.2172 3.82659 11.6169 3.82659 10.6862V4.82257Z" fill="#628478" stroke="#F6F8F7" strokeWidth="0.5"/>
                </svg>
              }
              style={{ padding: '4px 8px' }}
              className="btn-style-common"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const detailsPkg = detailsModal.pkg;
  const isDetailsDowngrade = detailsPkg
    ? (PKG_RANK[detailsPkg.key] ?? 0) < (PKG_RANK[activePackageKey] ?? 0)
    : false;
  const isDetailsActive = detailsPkg ? detailsPkg.key === activePackageKey : false;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SiderComponent props={props} />
      <Layout>
        <HeaderComponent />
        <Content style={{ backgroundColor: '#F5F5F5', padding: '32px 40px' }}>

          {/* Package Banner */}
          <PackageBanner
            packages={packages}
            activeKey={activePackageKey}
            pendingKey={pendingPackageKey}
            expiresAt={packageExpiresAt}
            onOpenDetails={handleOpenDetails}
            onOpenEnterpriseContact={handleOpenEnterpriseContact}
          />

          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: '24px 32px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 24,
              }}
            >
              <div>
                <h2 className="Form-heading">{t.heading}</h2>
                <p style={{ margin: 0, fontSize: 14, fontFamily: 'DM Sans', color: '#ff0404' }}>
                  {t.subHeading}
                </p>
              </div>
              <Button onClick={() => navigate('/deals/top-deals/new')} className="btn-style-common">
                {t.addDeal}
              </Button>
            </div>

            <Table
              rowKey="_id"
              columns={columns}
              dataSource={[...(golfClubDetails.topDeals || [])].sort((a, b) => isDealOnline(b) - isDealOnline(a))}
              pagination={false}
              style={{ marginTop: 8 }}
              className="top-deals-table"
            />
          </div>
        </Content>
      </Layout>

      {/* Upgrade modal */}
      <Modal
        visible={upgradeModal.visible}
        onCancel={() => setUpgradeModal({ visible: false, suggestedKeys: [] })}
        footer={null}
        title="Paket upgraden"
        width={upgradeModal.suggestedKeys.length > 1 ? 640 : 380}
      >
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', padding: '8px 0 16px' }}>
          {packages
            .filter((p) => upgradeModal.suggestedKeys.includes(p.key))
            .map((pkg) => {
              const colors = { pro: { border: '#4096ff', text: '#1677ff', bg: '#f0f7ff' }, premium: { border: '#9c5cf5', text: '#7c3aed', bg: '#f8f4ff' } };
              const c = colors[pkg.key] || { border: '#d9d9d9', text: '#555', bg: '#fff' };
              return (
                <div key={pkg.key} style={{ flex: 1, border: `2px solid ${c.border}`, borderRadius: 12, padding: '20px 16px', background: c.bg, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: c.text }}>{pkg.name}</div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{pkg.description}</div>
                  <FeatureList pkgKey={pkg.key} color={c.text} />
                  <div style={{ fontWeight: 700, fontSize: 15, color: c.text, marginTop: 8 }}>
                    {PKG_PRICES[pkg.key] || 'Kostenlos'}
                  </div>
                  <Button
                    type="primary"
                    style={{ marginTop: 8, background: c.border, borderColor: c.border }}
                    onClick={() => {
                      setUpgradeModal({ visible: false, suggestedKeys: [] });
                      const isDowngrade = (PKG_RANK[pkg.key] ?? 0) < (PKG_RANK[activePackageKey] ?? 0);
                      setActivateModal({ visible: true, pkg, isDowngrade });
                    }}
                  >
                    Paket buchen
                  </Button>
                </div>
              );
            })}
        </div>
      </Modal>

      {/* Details modal */}
      <Modal
        visible={detailsModal.visible}
        onCancel={() => setDetailsModal({ visible: false, pkg: null })}
        footer={null}
        title={detailsPkg?.name || 'Paket-Details'}
      >
        {detailsPkg && (
          <div>
            {detailsPkg.key !== 'enterprise' ? (
              <>
                <FeatureList pkgKey={detailsPkg.key} />
                {PKG_PRICE_AMOUNTS[detailsPkg.key] && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 14,
                    padding: '10px 14px',
                    background: '#f6f8f7',
                    borderRadius: 8,
                    fontSize: 13,
                  }}>
                    <span style={{ color: '#555' }}>Jahresgebühr (netto)</span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: (PKG_COLORS[detailsPkg.key] || PKG_COLORS.basic).text }}>
                      {PKG_PRICE_AMOUNTS[detailsPkg.key]}
                    </span>
                  </div>
                )}
                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 14 }}>
                  {isDetailsActive ? (
                    <p style={{ margin: 0, color: '#52C41A', fontWeight: 600 }}>
                      ✓ Dieses Paket ist aktuell aktiv.
                    </p>
                  ) : (
                    <>
                      {isDetailsDowngrade && (
                        <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                          Das Paket <strong>{detailsPkg.name}</strong> ist ein niedrigerer Tarif als Ihr aktuelles Paket.
                          Die Umstellung wird zum Ende Ihrer aktuellen Laufzeit{packageExpiresAt ? ` (${new Date(packageExpiresAt).toLocaleDateString('de-DE')})` : ''} wirksam.
                        </p>
                      )}
                      <Button
                        type="primary"
                        onClick={handleActivateFromDetails}
                        style={{ marginRight: 8 }}
                      >
                        {isDetailsDowngrade ? 'Tarif vormerken' : 'Jetzt verbindlich buchen'}
                      </Button>
                      <Button onClick={() => setDetailsModal({ visible: false, pkg: null })}>
                        Abbrechen
                      </Button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div>
                <p style={{ color: '#555', marginBottom: 16 }}>
                  Nehmen Sie Kontakt mit unserem Team auf, um für Ihre individuellen Bedürfnisse die passende Lösung zu finden.
                </p>
                <a
                  href="mailto:enterprise@greenfee-deals.de?subject=Anfrage%20zur%20Enterprise-L%C3%B6sung%20der%20Top%20Deals"
                  style={{ color: '#d4900a', fontWeight: 600 }}
                >
                  enterprise@greenfee-deals.de
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Package activation confirmation modal */}
      <Modal
        visible={activateModal.visible}
        onCancel={() => setActivateModal({ visible: false, pkg: null, isDowngrade: false })}
        onOk={handleActivatePackage}
        confirmLoading={loadingActivate}
        okText={activateModal.isDowngrade ? 'Jetzt vormerken' : 'Paket aktivieren'}
        cancelText="Abbrechen"
        title={
          activateModal.isDowngrade
            ? `Tarifwechsel zu „${activateModal.pkg?.name}" vormerken`
            : `Paket „${activateModal.pkg?.name}" verbindlich aktivieren?`
        }
      >
        {activateModal.pkg && (
          <div>
            {activateModal.isDowngrade ? (
              <>
                <p style={{ color: '#333', marginBottom: 12 }}>
                  Möchten Sie zum Tarif <strong>{activateModal.pkg.name}</strong> wechseln?
                </p>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
                  Die Umstellung startet automatisch nach Ablauf Ihres aktuellen Pakets
                  {packageExpiresAt ? ` am ${new Date(packageExpiresAt).toLocaleDateString('de-DE')}` : ''}.
                </p>
                <p style={{ fontSize: 12, color: '#888', borderTop: '1px solid #f0f0f0', paddingTop: 10, margin: 0 }}>
                  Bis dahin bleiben alle Funktionen Ihres aktuellen Pakets erhalten.
                </p>
              </>
            ) : (
              <>
                <p style={{ color: '#333', marginBottom: 12, fontWeight: 600 }}>
                  Möchten Sie das Paket <strong>{activateModal.pkg.name}</strong> jetzt verbindlich aktivieren?
                </p>
                <FeatureList pkgKey={activateModal.pkg.key} />
                <p style={{ fontSize: 12, color: '#888', borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 12, marginBottom: 0 }}>
                  Das Paket wird sofort aktiviert. Unser Team meldet sich zur Abrechnung bei Ihnen.
                </p>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Hervorheben-Bestätigung */}
      <Modal
        visible={featuredConfirmModal.visible}
        onCancel={() => setFeaturedConfirmModal({ visible: false, deal: null, remaining: 0 })}
        footer={null}
        title="Deal hervorheben?"
        width={400}
      >
        <p style={{ color: '#333', marginBottom: 6 }}>
          Sie haben noch <strong>{featuredConfirmModal.remaining}</strong> Top-Platzierung{featuredConfirmModal.remaining !== 1 ? 'en' : ''} übrig.
        </p>
        <p style={{ color: '#555', marginBottom: 20 }}>Wollen Sie diesen Deal hervorheben?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="primary" onClick={handleFeatureConfirmed}>Hervorheben</Button>
          <Button onClick={() => setFeaturedConfirmModal({ visible: false, deal: null, remaining: 0 })}>Abbrechen</Button>
        </div>
      </Modal>

      {/* Featured period modal (Premium) */}
      <Modal
        visible={featuredModal.visible}
        onCancel={() => setFeaturedModal({ visible: false, deal: null })}
        onOk={() => {
          if (!featuredRange || featuredRange.length !== 2) {
            message.warning('Bitte Zeitraum auswählen');
            return;
          }
          const period = {
            from: featuredRange[0].toDate(),
            to: featuredRange[1].toDate(),
          };
          const ppd = activePackageKey === 'premium' ? powerPushDays : null;
          activateFeatured(featuredModal.deal._id, period, ppd);
          setFeaturedModal({ visible: false, deal: null });
        }}
        confirmLoading={loadingFeatured}
        okText="Hervorhebung aktivieren"
        cancelText="Abbrechen"
        title="Top-Platzierung einstellen"
      >
        <div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
              Hervorhebungszeitraum
            </label>
            <RangePicker
              style={{ width: '100%' }}
              value={featuredRange}
              onChange={setFeaturedRange}
              format="DD.MM.YYYY"
            />
          </div>
          {activePackageKey === 'premium' && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
                Power-Push: Wie viele Tage vor Deal-Beginn?
              </label>
              <InputNumber
                min={1}
                max={7}
                value={powerPushDays}
                onChange={setPowerPushDays}
                style={{ width: '100%' }}
                addonAfter="Tag(e) vorher"
              />
              <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                {powerPushDays === 1
                  ? 'Der Reminder-Push wird einen Tag vor Beginn des Deals gesendet.'
                  : `Der Reminder-Push wird ${powerPushDays} Tage vor Beginn des Deals gesendet.`}
                {' '}Maximum: 7 Tage.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Enterprise contact modal */}
      <Modal
        visible={enterpriseModal.visible}
        onCancel={() => setEnterpriseModal({ visible: false, contactPerson: '', phone: '', loading: false })}
        onOk={handleSendEnterpriseContact}
        confirmLoading={enterpriseModal.loading}
        okText="Absenden"
        cancelText="Abbrechen"
        title="Enterprise-Anfrage senden"
      >
        <p style={{ color: '#555', marginBottom: 20 }}>
          Hinterlassen Sie Ihre Kontaktdaten. Wir melden uns persönlich bei Ihnen, um die passende Lösung zu besprechen.
        </p>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
            Ansprechpartner *
          </label>
          <Input
            placeholder="Name des Ansprechpartners"
            value={enterpriseModal.contactPerson}
            onChange={(e) => setEnterpriseModal((m) => ({ ...m, contactPerson: e.target.value }))}
          />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>
            Rückrufnummer *
          </label>
          <Input
            placeholder="+49 ..."
            value={enterpriseModal.phone}
            onChange={(e) => setEnterpriseModal((m) => ({ ...m, phone: e.target.value }))}
          />
        </div>
      </Modal>

      <Modal
        title={powerPushModal.date ? 'Power-Push Datum ändern' : 'Power-Push Datum wählen'}
        visible={powerPushModal.visible}
        onCancel={() => setPowerPushModal({ visible: false, deal: null, date: null, loading: false })}
        onOk={savePowerPush}
        okText="Bestätigen"
        cancelText="Abbrechen"
        confirmLoading={powerPushModal.loading}
        okButtonProps={{ disabled: !powerPushModal.date }}
      >
        <p style={{ color: '#555', marginBottom: 16 }}>
          Wähle das Datum, an dem der Power-Push gesendet werden soll.
          {powerPushModal.deal?.dealPeriod?.to && (
            <span> Spätestens bis zum Ende des Ereignisses ({new Date(powerPushModal.deal.dealPeriod.to).toLocaleDateString('de-DE')}).</span>
          )}
        </p>
        <DatePicker
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
          value={powerPushModal.date ? moment(powerPushModal.date) : null}
          onChange={(m) => setPowerPushModal((s) => ({ ...s, date: m ? m.toDate() : null }))}
          disabledDate={(current) => {
            if (!current) return false;
            if (current < moment().startOf('day')) return true;
            if (powerPushModal.deal?.dealPeriod?.to) {
              return current > moment(powerPushModal.deal.dealPeriod.to).endOf('day');
            }
            return false;
          }}
        />
      </Modal>

      <style jsx global>{`
        .top-deals-table .ant-table { font-size: 14px; }
        .top-deals-table table { border: 1px solid #00000010; border-radius: 10px; overflow: hidden; font-family: 'DM Sans' !important; }
        .top-deals-table thead { background-color: #f6f8f7; }
        .top-deals-table .ant-table-thead > tr > th { background-color: #f6f8f7; color: #628478; font-weight: 500; font-size: 13px; letter-spacing: 0.5px; padding: 12px 16px; border-bottom: 1px solid #f0f0f0; }
        .top-deals-table .ant-table-tbody > tr > td { padding: 16px; border-bottom: 1px solid #f0f0f0; }
        .top-deals-table .ant-table-tbody > tr:hover > td { background-color: #fafafa; }
      `}</style>
    </Layout>
  );
}

export default TopDealsList;
